import type { ICellRendererParams } from "ag-grid-community";
import { Col, Row, Typography } from "antd";
import type { Dayjs } from "dayjs";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import DeleteIcon from "../../../../assets/svg/bookingform/deleteIcon";
import { AppButton } from "../../../atoms/Button";
import Grid from "../../../atoms/Grid";
import { convertToTitleCase, formatCurrency, formatDateByType } from "../../../libs/utils";
import { ClearingDatePicker, isClearingDateValid } from "./ClearingDatePicker";
import { getReceiptBulkRowKey } from "./utils";

export type BulkClearedReceiptRow = {
  paymentReceiptId?: string;
  receiptNo?: string;
  invoiceNumber?: string;
  invoiceNo?: string;
  propertyDetails?: { propertyName?: string };
  propertyName?: string;
  tenantDetails?: { tenantNameEN?: string };
  tenantName?: string;
  unitCode?: string;
  modeOfPayment?: string;
  paymentMode?: string;
  paymentStatus?: string;
  receiptStatus?: string;
  isCleared?: boolean;
  totalAmount?: number;
  pdc?: {
    chequeNumber?: string;
  };
  paymentDetails?: {
    documentNo?: string;
    depositDate?: string;
  };
  _id?: string;
  beneficiary?: {
    accountTitle?: string;
    accountNumber?: string;
    bankName?: string;
    iban?: string;
  };
};

type BulkClearedReceiptsConfirmProps = {
  receipts: BulkClearedReceiptRow[];
  onConfirm: (paymentReceiptIds: string[], clearedDate: string) => void | Promise<unknown>;
  onClose: () => void;
  isLoading?: boolean;
};

const GRID_HEADER_PX = 42;
const GRID_ROW_PX = 42;
const GRID_MAX_VISIBLE_ROWS = 8;
const GRID_FIXED_HEIGHT_PX = GRID_HEADER_PX + GRID_MAX_VISIBLE_ROWS * GRID_ROW_PX;

export const BulkClearedReceiptsConfirm = (props: BulkClearedReceiptsConfirmProps) => {
  const { receipts, onConfirm, onClose, isLoading } = props;

  const [rows, setRows] = useState<BulkClearedReceiptRow[]>(() => receipts);
  const [clearingDate, setClearingDate] = useState<Dayjs | null>(null);
  const [showClearingDateError, setShowClearingDateError] = useState(false);

  useEffect(() => {
    setRows(receipts);
    setClearingDate(null);
    setShowClearingDateError(false);
  }, [receipts]);

  const removeRow = useCallback((rowKey: string) => {
    setRows((prev) => prev.filter((r) => getReceiptBulkRowKey(r) !== rowKey));
  }, []);

  const data = useMemo(
    () =>
      rows.map((row) => ({
        rowKey: getReceiptBulkRowKey(row),
        receiptNo: row.receiptNo ?? "",
        invoiceNumber: row.invoiceNumber ?? row.invoiceNo ?? "",
        propertyName: row.propertyDetails?.propertyName ?? row.propertyName ?? "",
        tenantName: row.tenantDetails?.tenantNameEN ?? row.tenantName ?? "",
        unitCode: row.unitCode ?? "",
        chequeNo: row?.pdc?.chequeNumber ?? "",
        paymentRef: row?.paymentDetails?.documentNo ?? "",
        paymentMode: row.modeOfPayment ?? row.paymentMode ?? "",
        clearedStatus: row.isCleared === true ? "Cleared" : "Not Cleared",
        receiptStatus: row.receiptStatus ?? row.paymentStatus ?? "",
        totalAmount: row.totalAmount,
        beneficiaryName: row?.beneficiary?.accountTitle ?? "",
        beneficiaryAccount: row?.beneficiary?.accountNumber ?? "",
        beneficiaryBank: row?.beneficiary?.bankName ?? "",
        beneficiaryIban: row?.beneficiary?.iban ?? "",
      })),
    [rows],
  );

  const columns = useMemo(
    () => [
      {
        field: "receiptNo",
        headerName: "Receipt No",
        flex: 0.85,
        minWidth: 150,
        maxWidth: 150,
        valueFormatter: ({ value }: { value?: string }) => (value ? `Rec_${value}` : "--"),
      },
      {
        field: "invoiceNumber",
        headerName: "Invoice No",
        flex: 0.9,
        minWidth: 150,
        maxWidth: 150,
      },
      { field: "propertyName", headerName: "Property", flex: 1, minWidth: 150 },
      { field: "tenantName", headerName: "Tenant Name", flex: 1.2, minWidth: 150 },
      { field: "unitCode", headerName: "Unit Code", flex: 0.85, maxWidth: 200 },
      {
        field: "paymentMode",
        headerName: "Payment Mode",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "chequeNo",
        headerName: "Cheque No",
        flex: 1,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        field: "paymentRef",
        headerName: "Payment Ref",
        flex: 1,
        minWidth: 150,
        maxWidth: 150,
      },
      {
        field: "receiptStatus",
        headerName: "Receipt Status",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryName",
        headerName: "Beneficiary Name",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryAccount",
        headerName: "Beneficiary Account",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryBank",
        headerName: "Beneficiary Bank",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "beneficiaryIban",
        headerName: "Beneficiary IBAN",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        valueFormatter: ({ value }: { value?: string }) => convertToTitleCase(value) || "--",
      },
      {
        field: "totalAmount",
        headerName: "Total Amount",
        flex: 1,
        minWidth: 200,
        maxWidth: 200,
        type: "rightAligned",
        valueFormatter: ({ value }: { value?: number }) => formatCurrency(value),
        cellStyle: { textAlign: "right" },
      },

      {
        colId: "remove",
        field: "rowKey",
        headerName: "",
        width: 55,
        maxWidth: 55,
        minWidth: 55,
        flex: 0,
        sortable: false,
        resizable: false,
        suppressMenu: true,
        pinned: "right",
        cellStyle: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "visible",
        },
        cellRenderer: (params: ICellRendererParams) => {
          const rowKey = params.data?.rowKey as string | undefined;
          if (!rowKey) return null;
          return (
            <button
              type="button"
              style={{
                border: "none",
                background: "transparent",
                cursor: "pointer",
                padding: 6,
                margin: 0,
                lineHeight: 0,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                removeRow(rowKey);
              }}
              aria-label="Remove from clear list"
            >
              <DeleteIcon style={{ margin: 0, display: "block" }} width={18} height={18} />
            </button>
          );
        },
      },
    ],
    [removeRow],
  );

  const handleClearingDateChange = (date: Dayjs | null) => {
    setClearingDate(date);
    if (isClearingDateValid(date)) {
      setShowClearingDateError(false);
    }
  };

  const handleConfirm = () => {
    if (!isClearingDateValid(clearingDate)) {
      setShowClearingDateError(true);
      return;
    }

    const clearedDate = formatDateByType(clearingDate, "api")!;
    const paymentReceiptIds = rows.map((r) => getReceiptBulkRowKey(r)).filter(Boolean);
    void onConfirm(paymentReceiptIds, clearedDate);
  };

  return (
    <div>
      {rows.length > 0 && (
        <p style={{ margin: 0, paddingTop: 10 }}>
          The {rows.length} receipt{rows.length === 1 ? "" : "s"} below will be marked as cleared. Remove any you do not
          want to include before confirming.
        </p>
      )}
      <div className="bulk-cleared-receipts-confirm-grid" style={{ height: GRID_FIXED_HEIGHT_PX }}>
        {rows.length === 0 ? (
          <div
            style={{
              height: GRID_FIXED_HEIGHT_PX,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: "0 12px",
              textAlign: "center",
            }}
          >
            <Typography.Paragraph type="secondary" style={{ margin: 0 }}>
              No receipts left in this list. Cancel and select again, or close the dialog.
            </Typography.Paragraph>
          </div>
        ) : (
          <Grid
            className="pl-0 pr-0"
            columns={columns}
            data={data}
            height={GRID_FIXED_HEIGHT_PX}
            headerHeight={GRID_HEADER_PX}
            rowHeight={GRID_ROW_PX}
          />
        )}
      </div>
      <Row gutter={24} align="middle" justify="space-between" style={{ marginTop: "3.5rem" }}>
        <Col>
          <AppButton variant="secondary" onClick={onClose} disabled={isLoading}>
            Cancel
          </AppButton>
        </Col>
        <Col>
          <div style={{ display: "flex", alignItems: "center", gap: "1rem" }}>
            <ClearingDatePicker
              value={clearingDate}
              onChange={handleClearingDateChange}
              showValidation={showClearingDateError}
            />
            <AppButton onClick={handleConfirm} disabled={rows.length === 0} loading={isLoading}>
              Confirm and Clear
            </AppButton>
          </div>
        </Col>
      </Row>
    </div>
  );
};
